import { useCallback, useEffect, useRef, useState } from 'react'

import axios from '../dataClient/dataClient'
import { handleDownloadResponse } from '../util/downloadFile'

const FIT_WIDTH_FRAGMENT = '#view=fitH'

const contentVersion = file => file?.contentUpdatedAt ?? file?.updatedAt ?? String(file?.lastModified ?? '')

// Manages the shared in-tab PDF preview and one-time search preview requests.
export default function useInlineFilePreview({ files, preview = null, onPreviewConsumed }) {
  const loadedFiles = Array.isArray(files) ? files : null
  const [activeFile, setActiveFile] = useState()
  const [pdfUrl, setPdfUrl] = useState()
  const [error, setError] = useState(null)
  // Ignore stale downloads and responses received after unmount.
  const requestSeq = useRef(0)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      requestSeq.current += 1
    }
  }, [])
  const [pdfFragment, setPdfFragment] = useState(FIT_WIDTH_FRAGMENT)
  const [matchPage, setMatchPage] = useState(null)
  const [jumpCount, setJumpCount] = useState(0)
  const previewRef = useRef(null)
  const scrollToPreviewRef = useRef(false)

  const close = useCallback(() => {
    requestSeq.current += 1
    setActiveFile()
    setPdfUrl()
    setPdfFragment(FIT_WIDTH_FRAGMENT)
    setMatchPage(null)
    setError(null)
  }, [])

  const showFromResponse = useCallback((downloadResult, file, page = null) => {
    const fileUrl = handleDownloadResponse(downloadResult, file.filename, 'view')
    setPdfFragment(page ? `#page=${page}&view=fitH` : FIT_WIDTH_FRAGMENT)
    setMatchPage(page ?? null)
    setPdfUrl(fileUrl)
    setActiveFile(file)
    setError(null)
  }, [])

  const failed = useCallback((file, e) => {
    console.error('An error occurred while fetching the document', file?.filename, e)
    setPdfUrl()
    setActiveFile()
    setError({ filename: file?.filename ?? null })
  }, [])

  const openFile = useCallback(
    async (file, page = null) => {
      const seq = ++requestSeq.current
      try {
        const response = await axios.get(`/file/${file.id}/download`, { responseType: 'blob' })
        if (seq !== requestSeq.current || !mountedRef.current) return
        showFromResponse(response, file, page)
      } catch (e) {
        if (seq !== requestSeq.current || !mountedRef.current) return
        failed(file, e)
      }
    },
    [showFromResponse, failed]
  )

  const jumpToMatch = useCallback(() => {
    if (!matchPage) return
    setJumpCount(count => count + 1)
    previewRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [matchPage])

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
    }
  }, [pdfUrl])

  // Refresh the preview when a replacement changes the file content version.
  useEffect(() => {
    if (!activeFile || !loadedFiles) return
    const fresh = loadedFiles.find(f => f.id === activeFile.id)
    if (!fresh) {
      if (!activeFile.provisional) close()
      return
    }
    if (fresh === activeFile) return
    // Adopt a provisional file record without downloading the same content again.
    if (activeFile.provisional || contentVersion(fresh) === contentVersion(activeFile)) {
      setActiveFile(fresh)
      return
    }
    openFile(fresh, matchPage)
  }, [loadedFiles])

  // Open each search result or deep-link request once.
  const filesRef = useRef(loadedFiles)
  filesRef.current = loadedFiles
  const previewFilePresent = Boolean(preview?.fileId && loadedFiles?.some(f => f.id === preview.fileId))
  const honouredRequest = useRef(null)
  useEffect(() => {
    if (!preview?.fileId) return
    const requestKey = `${preview.fileId}|${preview.nonce}`
    if (honouredRequest.current === requestKey) return
    const listed = filesRef.current?.find(f => f.id === preview.fileId)
    const file = listed ?? (preview.filename ? { id: preview.fileId, filename: preview.filename, fileExtension: '.pdf', provisional: true } : null)
    if (!file || file.fileExtension !== '.pdf') return
    honouredRequest.current = requestKey
    const seq = ++requestSeq.current
    axios
      .get(`/file/${file.id}/download`, { responseType: 'blob' })
      .then(response => {
        if (seq !== requestSeq.current || !mountedRef.current) return
        scrollToPreviewRef.current = true
        showFromResponse(response, file, preview.page)
      })
      .catch(e => {
        if (seq !== requestSeq.current || !mountedRef.current) return
        failed(file, e)
      })
    onPreviewConsumed?.()
  }, [preview?.fileId, preview?.page, preview?.nonce, preview?.filename, previewFilePresent, showFromResponse, failed, onPreviewConsumed])

  useEffect(() => {
    if (!pdfUrl || !scrollToPreviewRef.current) return
    scrollToPreviewRef.current = false
    previewRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [pdfUrl])

  return {
    activeFile,
    pdfUrl,
    error,
    pdfFragment,
    matchPage,
    previewRef,
    viewerKey: `${pdfUrl ?? ''}|${pdfFragment}|${jumpCount}`,
    showFromResponse,
    openFile,
    jumpToMatch,
    close
  }
}
